/* eslint-disable max-lines */

import { EnvelopeAction, UUID } from '@litlingo/client';
import { deleteEnvelopeAction, updateEnvelopeAction } from 'actions/envelopeActions';
import { NEW_DELETE_ICON, NEW_EDIT_ICON } from 'constants/commonIcons';
import moment from 'moment';
import React, { Dispatch, SetStateAction, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getUser } from 'selectors/auth';
import { useSelector } from 'store';

import { InitialConfigType, LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';

import { $generateHtmlFromNodes } from '@lexical/html';
import { getUsers } from 'selectors/users';
import ExecuteOnEnterPlugin from 'utils/lexical/ExecuteOnEnterPlugin';
import HandleModifyMentionsPlugin from 'utils/lexical/HandleModifyMentionsPlugin';
import LoadHtmlPlugin from 'utils/lexical/LoadHtmlPlugin';
import { MentionNode } from 'utils/lexical/MentionNode';
import MentionsPlugin from 'utils/lexical/MentionsPlugin';

type ComponentProps = {
  comment: EnvelopeAction;
  index: number;
  arr: EnvelopeAction[];
  editingId: string;
  setEditingId: Dispatch<SetStateAction<string>>;
};

const initialConfig: InitialConfigType = {
  namespace: 'main-comment-editor',
  nodes: [MentionNode],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onError: (error: any): void => {
    throw error;
  },
};

const commentConfig: InitialConfigType = {
  namespace: 'main-comment-editor',
  nodes: [MentionNode],
  editable: false,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onError: (error: any): void => {
    throw error;
  },
};

const EnvelopeSidebarCommentItem: React.FC<ComponentProps> = ({
  comment,
  index,
  arr,
  editingId,
  setEditingId,
}) => {
  const dispatch = useDispatch();
  const { envelopeId } = useParams<{ envelopeId: string }>();

  const [commentValue, setCommentValue] = useState<{
    value: string;
    taggedUsers: { name: string; email: string; uuid: string }[];
  }>({
    value: comment.value || '',
    // @ts-ignore
    taggedUsers: comment.payload?.taggedUsers || [],
  });
  const [hover, setHover] = useState(false);

  const user = useSelector(getUser);
  const users = useSelector(getUsers);

  const {
    uuid,
    value,
    updated_by: updatedBy,
    created_by: createdBy,
    created_by_uuid: createdById,
    updated_at: updatedAt,
    created_at: createdAt,
  } = comment;

  const name = updatedBy?.name || createdBy?.name;
  const date = updatedAt || createdAt;
  const isUpdated = moment
    .duration(moment(updatedAt).valueOf() - moment(createdAt).valueOf())
    .asMilliseconds();
  const time = moment(date).format('MMM DD | h:mm a');

  const handleDeleteCommet = (envActionUuid: UUID): void => {
    dispatch(deleteEnvelopeAction({ envActionUuid, envelopeId }));
  };

  const handleSaveEditComment = (text?: string): void => {
    const val = text || commentValue.value;
    dispatch(
      updateEnvelopeAction({ uuid, value: val, payload: { taggedUsers: commentValue.taggedUsers } })
    );
    setEditingId('');
  };

  const handleEditComment = (): void => {
    setCommentValue({ value: commentValue.value, taggedUsers: commentValue.taggedUsers });
    setEditingId(uuid);
  };

  const handleCancelEditComment = (): void => {
    setCommentValue({ value: commentValue.value, taggedUsers: commentValue.taggedUsers });
    setEditingId('');
  };

  const handleCreateMention = (commentName: string, email: string, val: string): void => {
    setCommentValue((c) => ({
      value: c.value,
      taggedUsers: [
        ...c.taggedUsers.filter((u) => u.uuid !== val),
        {
          name: commentName,
          email,
          uuid: val,
        },
      ],
    }));
  };

  const handleDeleteMention = (val: string): void => {
    setCommentValue((c) => ({
      value: c.value,
      taggedUsers: c.taggedUsers.filter((u) => u.uuid !== val),
    }));
  };

  return (
    <div
      className={`relative flex flex-col gap-1 rounded ${editingId !== uuid && 'py-1 pl-1 pr-2'}
           ${index % 2 === 0 ? 'bg-litlingo-gray-0.5' : 'bg-litlingo-gray-1'} ${
        index !== arr.length - 1 ? 'mb-1' : ''
      } `}
      style={{
        filter: hover ? 'drop-shadow(0px 0px 4px rgba(0, 0, 0, 0.5))' : '',
      }}
      onMouseEnter={(): void => {
        setHover(true);
      }}
      onMouseLeave={(): void => setHover(false)}
    >
      {hover && !editingId && createdById === user.uuid && (
        <div className="flex flex-row absolute right-1 top-1 gap-x-1">
          <button
            type="button"
            className="flex-none  bg-litlingo-gray-4 rounded px-1 w-5 h-5 focus:outline-none hover:bg-litlingo-gray-5"
            onClick={(): void => handleEditComment()}
          >
            <div>{NEW_EDIT_ICON('text-litlingo-white')}</div>
          </button>

          <button
            type="button"
            className="flex-none  bg-litlingo-gray-4 rounded px-1 w-5 h-5 focus:outline-none hover:bg-litlingo-gray-5"
            onClick={(): void => handleDeleteCommet(uuid)}
          >
            <div>{NEW_DELETE_ICON('text-litlingo-white')}</div>
          </button>
        </div>
      )}
      {editingId === uuid && (
        <div className="relative h-full">
          <div className="flex flex-col">
            <LexicalComposer initialConfig={initialConfig}>
              <LoadHtmlPlugin content={value || ''} />
              <MentionsPlugin
                initialOptions={Object.values(users).map((u) => ({
                  name: u.name || '',
                  email: u.email || '',
                  value: u.uuid || '',
                }))}
              />
              <HandleModifyMentionsPlugin
                createdCallback={handleCreateMention}
                destroyedCallback={handleDeleteMention}
              />
              <RichTextPlugin
                contentEditable={
                  <ContentEditable
                    aria-placeholder="Hit 'Enter' to save"
                    placeholder={
                      <div className="absolute italic" style={{ bottom: 4, right: 8 }}>
                        Hit &apos;Enter&apos; to save
                      </div>
                    }
                    className="min-h-20 p-2 text-body rounded border border-litlingo-gray-2"
                  />
                }
                ErrorBoundary={LexicalErrorBoundary}
              />
              <OnChangePlugin
                onChange={(_editorState, editor): void => {
                  editor.update(() => {
                    const htmlString = $generateHtmlFromNodes(editor);
                    setCommentValue({ value: htmlString, taggedUsers: commentValue.taggedUsers });
                  });
                }}
              />
              <ExecuteOnEnterPlugin callback={handleSaveEditComment} />
            </LexicalComposer>
          </div>
          <div className="absolute bottom-1 right-1 flex flex-row gap-x-1">
            <button
              type="button"
              className="button flex justify-center text-base leading-5 font-bold text-litlingo-gray-5 bg-white border-litlingo-gray-3 w-19 h-6 text-center"
              onClick={handleCancelEditComment}
            >
              Cancel
            </button>
            <button
              type="button"
              className="button button--primary flex justify-center text-base leading-5 font-bold text-white w-16 h-6"
              onClick={(): void => handleSaveEditComment()}
            >
              Save
            </button>
          </div>
        </div>
      )}
      {editingId !== uuid && (
        <>
          <div className="flex flex-row text-base text-litlingo-gray-6 font-normal leading-5">
            <LexicalComposer initialConfig={commentConfig}>
              <LoadHtmlPlugin edited={isUpdated !== 0} content={value || ''} />
              <MentionsPlugin
                initialOptions={Object.values(users).map((u) => ({
                  name: u.name || '',
                  email: u.email || '',
                  value: u.uuid || '',
                }))}
              />
              <RichTextPlugin
                contentEditable={
                  <ContentEditable
                    aria-placeholder="Hit 'Enter' to save"
                    placeholder=""
                    className=""
                  />
                }
                ErrorBoundary={LexicalErrorBoundary}
              />
            </LexicalComposer>
          </div>
          <div className="flex justify-end text-xss text-litlingo-gray-6 font-normal leading-4">
            <div>{`${name} on ${time}`}</div>
          </div>
        </>
      )}
    </div>
  );
};

export default EnvelopeSidebarCommentItem;
